import { useCallback, useMemo, useRef } from 'react'
import type { DragEvent, MouseEvent } from 'react'

import classNames from 'classnames'

import { useHorizontalDragScroll, useHorizontalOverflow } from '@app/hooks'

import sharedStyles from '../styles.module.scss'
import { FileAttachmentCard } from './FileAttachmentCard'
import { getFileIconPath, getFileTypeLabel } from './helpers'
import styles from './styles.module.scss'
import type { MessageFileAttachmentsProps } from './types'
import { openAttachmentWithAuth } from '../attachmentAuth'
import type { EnhancedFileAttachment } from '../types'

const decodeFileName = (value?: string) => {
  if (!value) {
    return undefined
  }

  try {
    return decodeURIComponent(value)
  } catch (error) {
    return value
  }
}

const getSourceFileName = (name?: string, url?: string) => {
  if (name?.trim()) {
    return decodeFileName(name.trim())
  }

  if (!url) {
    return undefined
  }

  try {
    const { pathname } = new URL(url)
    const fileName = pathname.split('/').filter(Boolean).pop()
    return decodeFileName(fileName ?? undefined)
  } catch (error) {
    const lastSegment = url.split('/').pop()
    if (!lastSegment) {
      return undefined
    }

    const cleanedSegment = lastSegment.split('?')[0]?.split('#')[0]
    return decodeFileName(cleanedSegment || undefined)
  }
}

const getFileNameWithoutExtension = (fileName?: string) => {
  if (!fileName) {
    return undefined
  }

  const lastDotIndex = fileName.lastIndexOf('.')
  if (lastDotIndex <= 0) {
    return fileName
  }

  return fileName.slice(0, lastDotIndex)
}

const getExtensionFromFileName = (fileName?: string) => {
  if (!fileName) {
    return undefined
  }

  const lastDotIndex = fileName.lastIndexOf('.')
  if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) {
    return undefined
  }

  return fileName.slice(lastDotIndex + 1)
}

export const MessageFileAttachments = ({ items }: MessageFileAttachmentsProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const hasHorizontalOverflow = useHorizontalOverflow(containerRef, [items.length])

  useHorizontalDragScroll(containerRef, {
    disabled: !hasHorizontalOverflow,
  })

  const handleFileAnchorDragStart = useCallback((event: DragEvent<HTMLAnchorElement>) => {
    event.preventDefault()
  }, [])

  const handleFileAnchorClick = useCallback(
    (item: EnhancedFileAttachment) =>
      async (event: MouseEvent<HTMLAnchorElement>) => {
        event.stopPropagation()
        event.preventDefault()

        try {
          const downloadName = item.title || item.displayName
          await openAttachmentWithAuth(item.url, { downloadName })
        } catch (error) {
          console.error('Failed to download file attachment', error)
        }
      },
    []
  )

  const fileItems = useMemo<EnhancedFileAttachment[]>(() => {
    return items.map((item) => {
      const sourceFileName = getSourceFileName(item.name, item.url)
      const displayNameSource =
        getFileNameWithoutExtension(sourceFileName) ?? sourceFileName ?? 'Attachment'
      const displayName = displayNameSource.trim() || 'Attachment'
      const fallbackExtension = getExtensionFromFileName(sourceFileName)
      const fileType = getFileTypeLabel(item.mimeType, fallbackExtension)
      const iconPath = getFileIconPath(item.mimeType, fileType)
      const title = sourceFileName || item.name || item.url

      return {
        ...item,
        displayName,
        fileType,
        iconPath,
        title,
      } as EnhancedFileAttachment
    })
  }, [items])

  if (!fileItems.length) {
    return null
  }

  const containerClassName = classNames(sharedStyles.attachmentsWrapper, {
    [sharedStyles.dragEnabled]: hasHorizontalOverflow,
  })

  return (
    <div
      className={containerClassName}
      ref={containerRef}
    >
      <div className={styles.fileAttachments}>
        {fileItems.map((item) => (
          <a
            key={`message-file-attachment-${item.id}`}
            className={styles.fileAttachment}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            draggable={false}
            onDragStart={handleFileAnchorDragStart}
            onClick={handleFileAnchorClick(item)}
            title={item.title}
          >
            <FileAttachmentCard
              displayName={item.displayName}
              fileType={item.fileType}
              iconPath={item.iconPath}
              title={item.title}
            />
          </a>
        ))}
      </div>
    </div>
  )
}
